import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import { Clock3 as ProfiliIcon, CalendarClock, Users, CalendarCheck2, ClipboardList, Edit as EditIcon, Delete as DeleteIcon, Search } from 'lucide-react';
import { profiliOrarioMock } from '../mocks/hr/personaleMockData';
import api from '../services/api';
import { toast } from 'react-toastify';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const GIORNI_SETTIMANA = [
  { key: 1, label: 'Lun' },
  { key: 2, label: 'Mar' },
  { key: 3, label: 'Mer' },
  { key: 4, label: 'Gio' },
  { key: 5, label: 'Ven' },
  { key: 6, label: 'Sab' },
  { key: 7, label: 'Dom' }
];

const GIORNO_LABEL_COMPLETO = {
  1: 'Lunedi',
  2: 'Martedi',
  3: 'Mercoledi',
  4: 'Giovedi',
  5: 'Venerdi',
  6: 'Sabato',
  7: 'Domenica'
};

function createDefaultFasceForm() {
  const out = {};
  GIORNI_SETTIMANA.forEach((g) => {
    out[g.key] = g.key <= 5
      ? [
          { start: '09:00', end: '13:00' },
          { start: '13:40', end: '18:00' }
        ]
      : [];
  });
  return out;
}

function minutesFromHHMM(value) {
  if (!value) return null;
  const parts = String(value).split(':');
  if (parts.length !== 2) return null;
  const hh = Number(parts[0]);
  const mm = Number(parts[1]);
  if (Number.isNaN(hh) || Number.isNaN(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return (hh * 60) + mm;
}

function fasceFormToPayload(fasceForm) {
  const out = [];
  GIORNI_SETTIMANA.forEach((g) => {
    const slots = Array.isArray(fasceForm[g.key]) ? fasceForm[g.key] : [];
    slots.forEach((slot, idx) => {
      if (slot.start && slot.end) {
        out.push({
          giorno_settimana: g.key,
          ordine: idx + 1,
          ora_inizio: slot.start,
          ora_fine: slot.end
        });
      }
    });
  });
  return out;
}

function fasceRowsToForm(rows = []) {
  const base = createDefaultFasceForm();
  GIORNI_SETTIMANA.forEach((g) => {
    base[g.key] = [];
  });
  rows.forEach((f) => {
    const day = Number(f.giorno_settimana);
    if (!base[day]) return;
    base[day].push({
      start: String(f.ora_inizio || '').slice(0, 5),
      end: String(f.ora_fine || '').slice(0, 5),
      ordine: Number(f.ordine || 1)
    });
  });
  GIORNI_SETTIMANA.forEach((g) => {
    base[g.key].sort((a, b) => (a.ordine || 0) - (b.ordine || 0));
    base[g.key] = base[g.key].map(({ start, end }) => ({ start, end }));
  });
  return base;
}

function getWeeklyMinutesFromFasce(fascePayload = []) {
  return fascePayload.reduce((acc, f) => {
    const start = minutesFromHHMM(f.ora_inizio);
    const end = minutesFromHHMM(f.ora_fine);
    if (start === null || end === null || end <= start) return acc;
    return acc + (end - start);
  }, 0);
}

function formatMinutesAsHours(minutes) {
  const safe = Math.max(0, Number(minutes || 0));
  const hh = Math.floor(safe / 60);
  const mm = safe % 60;
  return `${hh}h ${String(mm).padStart(2, '0')}m`;
}

function formatDecimalHoursToHM(value) {
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return '0h 00m';
  const total = Math.round(n * 60);
  return formatMinutesAsHours(total);
}

function formatDateIt(value) {
  if (!value) return '-';
  const str = String(value);
  const datePart = str.includes('T') ? str.split('T')[0] : str;
  const parts = datePart.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return str;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

const StatWidgetHr = ({ title, value, icon: Icon, color }) => (
  <Paper
    sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%' }}
    elevation={0}
    variant="outlined"
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export default function HrProfiliOrario() {
  const [tab, setTab] = useState('profili');
  const [loading, setLoading] = useState(true);
  const [loadingAssegnazioni, setLoadingAssegnazioni] = useState(true);
  const [loadingEccezioni, setLoadingEccezioni] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sourceMode, setSourceMode] = useState('api');
  const [profiliRows, setProfiliRows] = useState(profiliOrarioMock.profili || []);
  const [assegnazioniRows, setAssegnazioniRows] = useState(profiliOrarioMock.assegnazioni || []);
  const [assegnazioniTotal, setAssegnazioniTotal] = useState((profiliOrarioMock.assegnazioni || []).length);
  const [assegnazioniPage, setAssegnazioniPage] = useState(0);
  const [assegnazioniRowsPerPage, setAssegnazioniRowsPerPage] = useState(25);
  const [assegnazioniSearch, setAssegnazioniSearch] = useState('');
  const [eccezioniRows, setEccezioniRows] = useState(profiliOrarioMock.eccezioni || []);
  const [eccezioniTotal, setEccezioniTotal] = useState((profiliOrarioMock.eccezioni || []).length);
  const [eccezioniPage, setEccezioniPage] = useState(0);
  const [eccezioniRowsPerPage, setEccezioniRowsPerPage] = useState(25);
  const [eccezioniSearch, setEccezioniSearch] = useState('');
  const [fascePerProfilo, setFascePerProfilo] = useState({});
  const [dipendentiOptions, setDipendentiOptions] = useState([]);
  const [profiloSelezionato, setProfiloSelezionato] = useState((profiliOrarioMock.profili || [])[0] || null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteTargetTab, setDeleteTargetTab] = useState('profili');
  const [formProfilo, setFormProfilo] = useState({
    nome_profilo: '',
    tipologia_orario: 'fisso',
    ore_settimanali: '40',
    flessibile: 0,
    attivo: 1
  });
  const [formFasce, setFormFasce] = useState(createDefaultFasceForm());
  const [formAssegnazione, setFormAssegnazione] = useState({
    dipendente_id: '',
    profilo_orario_id: '',
    data_inizio: '',
    data_fine: ''
  });
  const [formEccezione, setFormEccezione] = useState({
    dipendente_id: '',
    data_eccezione: '',
    lavorativo: 1,
    motivo: ''
  });
  const [giornoFasceTab, setGiornoFasceTab] = useState(1);

  const normalizeNomeProfilo = useCallback(
    (nome) => String(nome || '').trim().toLowerCase().replace(/\s+/g, '_'),
    []
  );
  const isProfiloBaseSelected = normalizeNomeProfilo(profiloSelezionato?.nome) === 'profilo_base';

  const mapAssegnazioniRows = useCallback((rows) => (
    (rows || []).map((row) => ({
      id: row.id,
      dipendente: `${row.dipendente_cognome || ''} ${row.dipendente_nome || ''}`.trim() || '-',
      dipendente_id: row.dipendente_id,
      profilo: row.profilo_nome || '-',
      profilo_orario_id: row.profilo_orario_id,
      data_inizio: formatDateIt(row.data_inizio),
      data_fine: row.data_fine ? formatDateIt(row.data_fine) : '-',
      data_inizio_raw: row.data_inizio ? String(row.data_inizio).slice(0, 10) : '',
      data_fine_raw: row.data_fine ? String(row.data_fine).slice(0, 10) : ''
    }))
  ), []);

  const mapEccezioniRows = useCallback((rows) => (
    (rows || []).map((row) => ({
      id: row.id,
      dipendente: `${row.dipendente_cognome || ''} ${row.dipendente_nome || ''}`.trim() || '-',
      dipendente_id: row.dipendente_id,
      data: formatDateIt(row.data_eccezione),
      data_eccezione: row.data_eccezione ? String(row.data_eccezione).slice(0, 10) : '',
      lavorativo: Number(row.lavorativo) === 1 ? 'Si' : 'No',
      lavorativo_raw: Number(row.lavorativo) === 1 ? 1 : 0,
      motivo: row.motivo || '-'
    }))
  ), []);

  const loadAssegnazioni = useCallback(async () => {
    setLoadingAssegnazioni(true);
    try {
      if (sourceMode !== 'api') {
        const fallback = profiliOrarioMock.assegnazioni || [];
        const q = assegnazioniSearch.trim().toLowerCase();
        const selectedNome = String(profiloSelezionato?.nome || '').trim();
        const base = selectedNome ? fallback.filter((r) => String(r.profilo || '') === selectedNome) : fallback;
        const filtered = q
          ? base.filter((r) => String(r.dipendente || '').toLowerCase().includes(q) || String(r.profilo || '').toLowerCase().includes(q))
          : base;
        setAssegnazioniRows(filtered);
        setAssegnazioniTotal(filtered.length);
        return;
      }

      const res = await api.get('/assegnazioni-profilo-orario', {
        params: {
          q: assegnazioniSearch.trim(),
          profilo_orario_id: profiloSelezionato?.id || undefined,
          sortBy: 'dipendente',
          sortDir: 'ASC',
          limit: assegnazioniRowsPerPage,
          offset: assegnazioniPage * assegnazioniRowsPerPage
        }
      });

      const payload = res.data?.items ? res.data : { items: res.data || [], total: (res.data || []).length, limit: assegnazioniRowsPerPage, offset: assegnazioniPage * assegnazioniRowsPerPage };
      setAssegnazioniRows(mapAssegnazioniRows(payload.items));
      setAssegnazioniTotal(Number(payload.total || 0));
    } catch (error) {
      console.error('Errore caricamento assegnazioni profilo orario:', error);
      const fallback = profiliOrarioMock.assegnazioni || [];
      setAssegnazioniRows(fallback);
      setAssegnazioniTotal(fallback.length);
    } finally {
      setLoadingAssegnazioni(false);
    }
  }, [assegnazioniPage, assegnazioniRowsPerPage, assegnazioniSearch, mapAssegnazioniRows, profiloSelezionato?.id, profiloSelezionato?.nome, sourceMode]);

  const loadEccezioni = useCallback(async () => {
    setLoadingEccezioni(true);
    try {
      if (sourceMode !== 'api') {
        const fallback = profiliOrarioMock.eccezioni || [];
        const q = eccezioniSearch.trim().toLowerCase();
        const filtered = q
          ? fallback.filter((r) => String(r.dipendente || '').toLowerCase().includes(q) || String(r.motivo || '').toLowerCase().includes(q))
          : fallback;
        setEccezioniRows(filtered);
        setEccezioniTotal(filtered.length);
        return;
      }

      const res = await api.get('/eccezioni-profilo-orario', {
        params: {
          q: eccezioniSearch.trim(),
          profilo_orario_id: profiloSelezionato?.id || undefined,
          sortBy: 'dipendente',
          sortDir: 'ASC',
          limit: eccezioniRowsPerPage,
          offset: eccezioniPage * eccezioniRowsPerPage
        }
      });

      const payload = res.data?.items ? res.data : { items: res.data || [], total: (res.data || []).length, limit: eccezioniRowsPerPage, offset: eccezioniPage * eccezioniRowsPerPage };
      setEccezioniRows(mapEccezioniRows(payload.items));
      setEccezioniTotal(Number(payload.total || 0));
    } catch (error) {
      console.error('Errore caricamento eccezioni profilo orario:', error);
      const fallback = profiliOrarioMock.eccezioni || [];
      setEccezioniRows(fallback);
      setEccezioniTotal(fallback.length);
    } finally {
      setLoadingEccezioni(false);
    }
  }, [eccezioniPage, eccezioniRowsPerPage, eccezioniSearch, mapEccezioniRows, profiloSelezionato?.id, sourceMode]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [profRes, dipRes] = await Promise.all([
        api.get('/profili-orario'),
        api.get('/dipendenti', { params: { limit: 200, offset: 0, sortBy: 'cognome', sortDir: 'ASC' } })
      ]);

      const profili = (profRes.data || []).map((row) => ({
        id: row.id,
        nome: row.nome_profilo,
        tipologia: row.tipologia_orario,
        ore_settimanali_raw: Number(row.ore_settimanali || 0),
        ore_settimanali: formatDecimalHoursToHM(row.ore_settimanali),
        flessibile: Number(row.flessibile) === 1 ? 'Si' : 'No',
        attivo: Number(row.attivo) === 1 ? 'Si' : 'No'
      }));

      const rawProfili = profRes.data || [];
      const fasceEntries = await Promise.all(
        rawProfili.map(async (p) => {
          try {
            const res = await api.get(`/profili-orario/${p.id}/fasce`);
            return [String(p.id), res.data || []];
          } catch (e) {
            return [String(p.id), []];
          }
        })
      );
      const nextFasceMap = Object.fromEntries(fasceEntries);

      setProfiliRows(profili);
      setFascePerProfilo(nextFasceMap);
      const dipItems = dipRes.data?.items || [];
      setDipendentiOptions(
        dipItems.map((d) => ({
          id: String(d.id),
          label: `${d.cognome || ''} ${d.nome || ''}`.trim()
        }))
      );
      setProfiloSelezionato((prev) => {
        if (!profili.length) return null;
        if (!prev) return profili[0];
        return profili.find((p) => p.id === prev.id) || profili[0];
      });
      setSourceMode('api');
    } catch (error) {
      console.error('Errore caricamento profili orario:', error);
      setProfiliRows(profiliOrarioMock.profili || []);
      setAssegnazioniRows(profiliOrarioMock.assegnazioni || []);
      setAssegnazioniTotal((profiliOrarioMock.assegnazioni || []).length);
      setEccezioniRows(profiliOrarioMock.eccezioni || []);
      setEccezioniTotal((profiliOrarioMock.eccezioni || []).length);
      setFascePerProfilo({});
      setDipendentiOptions([]);
      setProfiloSelezionato((profiliOrarioMock.profili || [])[0] || null);
      setSourceMode('mock');
    } finally {
      setLoading(false);
    }
  }, [mapAssegnazioniRows]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadAssegnazioni();
  }, [loadAssegnazioni]);

  useEffect(() => {
    loadEccezioni();
  }, [loadEccezioni]);

  useEffect(() => {
    setAssegnazioniPage(0);
    setEccezioniPage(0);
  }, [profiloSelezionato?.id]);

  useEffect(() => {
    setAssegnazioniPage(0);
  }, [assegnazioniSearch, assegnazioniRowsPerPage]);

  useEffect(() => {
    setEccezioniPage(0);
  }, [eccezioniSearch, eccezioniRowsPerPage]);

  const kpi = useMemo(() => ({
    profili: profiliRows.length,
    assegnazioni: sourceMode === 'api' ? assegnazioniTotal : assegnazioniRows.length,
    eccezioni: sourceMode === 'api' ? eccezioniTotal : eccezioniRows.length
  }), [assegnazioniRows.length, assegnazioniTotal, eccezioniRows.length, eccezioniTotal, profiliRows.length, sourceMode]);
  const tableColSpan = tab === 'profili' ? 6 : (tab === 'eccezioni' ? 6 : 5);
  const weeklyMinutesForm = useMemo(
    () => getWeeklyMinutesFromFasce(fasceFormToPayload(formFasce)),
    [formFasce]
  );
  const fasceDettaglioByDay = useMemo(() => {
    if (!profiloSelezionato) return {};
    const map = {};
    GIORNI_SETTIMANA.forEach((g) => { map[g.key] = []; });
    (fascePerProfilo[String(profiloSelezionato.id)] || []).forEach((f) => {
      const day = Number(f.giorno_settimana);
      if (!map[day]) return;
      map[day].push({
        ordine: Number(f.ordine || 1),
        label: `${String(f.ora_inizio).slice(0, 5)} - ${String(f.ora_fine).slice(0, 5)}`
      });
    });
    GIORNI_SETTIMANA.forEach((g) => {
      map[g.key].sort((a, b) => a.ordine - b.ordine);
    });
    return map;
  }, [fascePerProfilo, profiloSelezionato]);

  const updateFasciaSlot = (dayKey, slotIndex, field, value) => {
    setFormFasce((prev) => {
      const next = { ...prev };
      const slots = Array.isArray(next[dayKey]) ? [...next[dayKey]] : [];
      slots[slotIndex] = { ...(slots[slotIndex] || { start: '', end: '' }), [field]: value };
      next[dayKey] = slots;
      return next;
    });
  };

  const addFasciaSlot = (dayKey) => {
    setFormFasce((prev) => {
      const next = { ...prev };
      const slots = Array.isArray(next[dayKey]) ? [...next[dayKey]] : [];
      slots.push({ start: '', end: '' });
      next[dayKey] = slots;
      return next;
    });
  };

  const removeFasciaSlot = (dayKey, slotIndex) => {
    setFormFasce((prev) => {
      const next = { ...prev };
      const slots = Array.isArray(next[dayKey]) ? [...next[dayKey]] : [];
      slots.splice(slotIndex, 1);
      next[dayKey] = slots;
      return next;
    });
  };

  const openCreateDialog = () => {
    setDialogMode('create');
    setEditingItem(null);
    if (tab === 'profili') {
      setGiornoFasceTab(1);
      setFormProfilo({
        nome_profilo: '',
        tipologia_orario: 'fisso',
        ore_settimanali: '40',
        flessibile: 0,
        attivo: 1
      });
      setFormFasce(createDefaultFasceForm());
    }
    if (tab === 'assegnazioni') {
      setFormAssegnazione({ dipendente_id: '', profilo_orario_id: '', data_inizio: '', data_fine: '' });
    }
    if (tab === 'eccezioni') {
      setFormEccezione({ dipendente_id: '', data_eccezione: '', lavorativo: 1, motivo: '' });
    }
    setDialogOpen(true);
  };

  const openEditDialog = (row) => {
    if (sourceMode !== 'api') {
      toast.warn('Modifica disponibile solo con API attiva');
      return;
    }
    setDialogMode('edit');
    setEditingItem(row);
    if (tab === 'profili') {
      setGiornoFasceTab(1);
      setFormProfilo({
        nome_profilo: row.nome || '',
        tipologia_orario: row.tipologia || 'fisso',
        ore_settimanali: String(row.ore_settimanali_raw ?? row.ore_settimanali ?? 40),
        flessibile: row.flessibile === 'Si' ? 1 : 0,
        attivo: row.attivo === 'Si' ? 1 : 0
      });
      setFormFasce(fasceRowsToForm(fascePerProfilo[String(row.id)] || []));
    }
    if (tab === 'assegnazioni') {
      setFormAssegnazione({
        dipendente_id: String(row.dipendente_id || ''),
        profilo_orario_id: String(row.profilo_orario_id || ''),
        data_inizio: row.data_inizio_raw && row.data_inizio_raw !== '-' ? row.data_inizio_raw : '',
        data_fine: row.data_fine_raw && row.data_fine_raw !== '-' ? row.data_fine_raw : ''
      });
    }
    if (tab === 'eccezioni') {
      setFormEccezione({
        dipendente_id: String(row.dipendente_id || ''),
        data_eccezione: row.data_eccezione && row.data_eccezione !== '-' ? row.data_eccezione : '',
        lavorativo: row.lavorativo_raw ?? (row.lavorativo === 'Si' ? 1 : 0),
        motivo: row.motivo && row.motivo !== '-' ? row.motivo : ''
      });
    }
    setDialogOpen(true);
  };

  const closeCreateDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const createLabel = useMemo(() => {
    if (tab === 'profili') return '+ Nuovo profilo';
    if (tab === 'assegnazioni') return '+ Nuova assegnazione';
    return '+ Nuova eccezione';
  }, [tab]);

  const dialogTitle = useMemo(() => {
    if (dialogMode === 'edit') {
      if (tab === 'profili') return 'Modifica profilo';
      if (tab === 'assegnazioni') return 'Modifica assegnazione';
      return 'Modifica eccezione';
    }
    return createLabel;
  }, [createLabel, dialogMode, tab]);

  const handleCreateSubmit = async () => {
    if (sourceMode !== 'api') {
      toast.warn('Operazione disponibile solo con API attiva');
      return;
    }
    try {
      setSaving(true);
      if (tab === 'profili') {
        if (!formProfilo.nome_profilo.trim()) {
          toast.error('Nome profilo obbligatorio');
          return;
        }
        const fascePayload = fasceFormToPayload(formFasce);
        const weeklyMinutes = getWeeklyMinutesFromFasce(fascePayload);
        const payload = {
          nome_profilo: formProfilo.nome_profilo.trim(),
          tipologia_orario: formProfilo.tipologia_orario,
          ore_settimanali: Number((weeklyMinutes / 60).toFixed(2)),
          flessibile: Number(formProfilo.flessibile),
          attivo: Number(formProfilo.attivo),
          fasce_settimanali: fascePayload
        };
        if (dialogMode === 'edit' && editingItem?.id) {
          await api.put(`/profili-orario/${editingItem.id}`, payload);
        } else {
          await api.post('/profili-orario', payload);
        }
      }
      if (tab === 'assegnazioni') {
        if (!formAssegnazione.dipendente_id || !formAssegnazione.profilo_orario_id || !formAssegnazione.data_inizio) {
          toast.error('Dipendente, profilo e data inizio sono obbligatori');
          return;
        }
        const payload = {
          dipendente_id: Number(formAssegnazione.dipendente_id),
          profilo_orario_id: Number(formAssegnazione.profilo_orario_id),
          data_inizio: formAssegnazione.data_inizio,
          data_fine: formAssegnazione.data_fine || null
        };
        if (dialogMode === 'edit' && editingItem?.id) {
          await api.put(`/assegnazioni-profilo-orario/${editingItem.id}`, payload);
        } else {
          await api.post('/assegnazioni-profilo-orario', payload);
        }
      }
      if (tab === 'eccezioni') {
        if (!formEccezione.dipendente_id || !formEccezione.data_eccezione) {
          toast.error('Dipendente e data eccezione sono obbligatori');
          return;
        }
        const payload = {
          dipendente_id: Number(formEccezione.dipendente_id),
          data_eccezione: formEccezione.data_eccezione,
          lavorativo: Number(formEccezione.lavorativo),
          motivo: formEccezione.motivo || null
        };
        if (dialogMode === 'edit' && editingItem?.id) {
          await api.put(`/eccezioni-profilo-orario/${editingItem.id}`, payload);
        } else {
          await api.post('/eccezioni-profilo-orario', payload);
        }
      }

      toast.success('Salvataggio completato');
      setDialogOpen(false);
      await loadData();
      await loadAssegnazioni();
      await loadEccezioni();
    } catch (error) {
      console.error('Errore creazione profili/assegnazioni/eccezioni:', error);
      toast.error(error.response?.data?.message || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (row) => {
    if (sourceMode !== 'api') {
      toast.warn('Eliminazione disponibile solo con API attiva');
      return;
    }
    setDeleteTarget(row);
    setDeleteTargetTab(tab);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      if (deleteTargetTab === 'profili') {
        await api.delete(`/profili-orario/${deleteTarget.id}`);
      } else if (deleteTargetTab === 'assegnazioni') {
        await api.delete(`/assegnazioni-profilo-orario/${deleteTarget.id}`);
      } else {
        await api.delete(`/eccezioni-profilo-orario/${deleteTarget.id}`);
      }
      toast.success('Eliminazione completata');
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      await loadData();
      await loadAssegnazioni();
      await loadEccezioni();
    } catch (error) {
      console.error('Errore eliminazione:', error);
      toast.error(error.response?.data?.message || 'Errore durante eliminazione');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <ProfiliIcon size={20} color="#4f46e5" />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Profili orario
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Profili contrattuali, assegnazioni storiche ed eccezioni giornaliere.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <StatWidgetHr title="Profili orario" value={kpi.profili} icon={CalendarClock} color="#6366f1" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatWidgetHr title="Assegnazioni" value={kpi.assegnazioni} icon={Users} color="#22c55e" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatWidgetHr title="Eccezioni" value={kpi.eccezioni} icon={CalendarCheck2} color="#f59e0b" />
        </Grid>
      </Grid>

      <StyledCard sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab label="Profili" value="profili" />
          <Tab label="Assegnazioni" value="assegnazioni" />
          <Tab label="Eccezioni" value="eccezioni" />
        </Tabs>
      </StyledCard>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <StyledCard>
            <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid rgba(145, 158, 171, 0.24)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '50%',
                    bgcolor: 'rgba(99,102,241,0.10)',
                    color: '#6366f1',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ClipboardList size={16} />
                </Box>
                <Typography variant="h6">
                  {tab === 'profili' && 'Profili orario'}
                  {tab === 'assegnazioni' && 'Assegnazioni profili'}
                  {tab === 'eccezioni' && 'Eccezioni giornaliere'}
                </Typography>
                {(tab === 'assegnazioni' || tab === 'eccezioni') && (
                  <Chip
                    size="small"
                    color={isProfiloBaseSelected ? 'success' : 'default'}
                    variant={isProfiloBaseSelected ? 'filled' : 'outlined'}
                    label={profiloSelezionato?.nome ? `Profilo: ${profiloSelezionato.nome}` : 'Profilo: seleziona un profilo'}
                  />
                )}
                {tab === 'assegnazioni' && (
                  <TextField
                    size="small"
                    placeholder="Cerca per dipendente o profilo..."
                    value={assegnazioniSearch}
                    onChange={(e) => setAssegnazioniSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={18} />
                        </InputAdornment>
                      )
                    }}
                    sx={{ minWidth: 320 }}
                  />
                )}
                {tab === 'eccezioni' && (
                  <TextField
                    size="small"
                    placeholder="Cerca per dipendente o motivo..."
                    value={eccezioniSearch}
                    onChange={(e) => setEccezioniSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={18} />
                        </InputAdornment>
                      )
                    }}
                    sx={{ minWidth: 320 }}
                  />
                )}
              </Box>
              <Button variant="contained" size="small" onClick={openCreateDialog}>
                {createLabel}
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'rgba(145, 158, 171, 0.08)' }}>
                  {tab === 'profili' && (
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Tipologia</TableCell>
                      <TableCell>Ore settimanali</TableCell>
                      <TableCell>Flessibile</TableCell>
                      <TableCell>Attivo</TableCell>
                      <TableCell align="left">Azioni</TableCell>
                    </TableRow>
                  )}
                  {tab === 'assegnazioni' && (
                    <TableRow>
                      <TableCell>Dipendente</TableCell>
                      <TableCell>Profilo</TableCell>
                      <TableCell>Data inizio</TableCell>
                      <TableCell>Data fine</TableCell>
                      <TableCell align="left">Azioni</TableCell>
                    </TableRow>
                  )}
                  {tab === 'eccezioni' && (
                    <TableRow>
                      <TableCell>Dipendente</TableCell>
                      <TableCell>Profilo</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Lavorativo</TableCell>
                      <TableCell>Motivo</TableCell>
                      <TableCell align="left">Azioni</TableCell>
                    </TableRow>
                  )}
                </TableHead>
                <TableBody>
                  {((tab === 'assegnazioni' ? loadingAssegnazioni : (tab === 'eccezioni' ? loadingEccezioni : loading))) && (
                    <TableRow>
                      <TableCell colSpan={tableColSpan} align="center" sx={{ py: 2.5 }}>
                        <CircularProgress size={22} />
                      </TableCell>
                    </TableRow>
                  )}
                  {tab === 'profili' &&
                     !loading &&
                    profiliRows.map((row) => (
                      <TableRow
                        key={row.id || row.nome}
                        hover
                        onClick={() => setProfiloSelezionato(row)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: profiloSelezionato?.nome === row.nome ? 'rgba(79, 70, 229, 0.08)' : 'transparent'
                        }}
                      >
                        <TableCell>{row.nome}</TableCell>
                        <TableCell>{row.tipologia}</TableCell>
                        <TableCell>{row.ore_settimanali}</TableCell>
                        <TableCell>{row.flessibile}</TableCell>
                        <TableCell>
                          <Chip size="small" label={row.attivo} color="success" variant="outlined" />
                        </TableCell>
                        <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                            <Tooltip title="Modifica">
                              <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openEditDialog(row); }} sx={{ mr: 1 }}>
                                <EditIcon size={18} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Elimina">
                              <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openDeleteDialog(row); }}>
                                <DeleteIcon size={18} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  {tab === 'assegnazioni' &&
                     !loadingAssegnazioni &&
                    assegnazioniRows.map((item) => (
                      <TableRow key={item.id || `${item.dipendente}-${item.profilo}`}>
                        <TableCell>{item.dipendente}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={item.profilo}
                            color={normalizeNomeProfilo(item.profilo) === 'profilo_base' ? 'success' : 'default'}
                            variant={normalizeNomeProfilo(item.profilo) === 'profilo_base' ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>{item.data_inizio}</TableCell>
                        <TableCell>{item.data_fine}</TableCell>
                        <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                            <Tooltip title="Modifica">
                              <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openEditDialog(item); }} sx={{ mr: 1 }}>
                                <EditIcon size={18} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Elimina">
                              <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openDeleteDialog(item); }}>
                                <DeleteIcon size={18} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  {tab === 'eccezioni' &&
                    !loadingEccezioni &&
                    eccezioniRows.map((item) => (
                      <TableRow key={item.id || `${item.dipendente}-${item.data}`}>
                        <TableCell>{item.dipendente}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={profiloSelezionato?.nome || '-'}
                            color={isProfiloBaseSelected ? 'success' : 'default'}
                            variant={isProfiloBaseSelected ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>{item.data}</TableCell>
                        <TableCell>{item.lavorativo}</TableCell>
                        <TableCell>{item.motivo}</TableCell>
                        <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                            <Tooltip title="Modifica">
                              <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openEditDialog(item); }} sx={{ mr: 1 }}>
                                <EditIcon size={18} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Elimina">
                              <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openDeleteDialog(item); }}>
                                <DeleteIcon size={18} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  {!loading &&
                    tab === 'profili' &&
                    profiliRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={tableColSpan} align="center">
                          <Typography variant="body2" color="text.secondary">Nessun profilo orario disponibile</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  {!loadingAssegnazioni &&
                    tab === 'assegnazioni' &&
                    assegnazioniRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={tableColSpan} align="center">
                          <Typography variant="body2" color="text.secondary">Nessuna assegnazione disponibile</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  {!loadingEccezioni &&
                    tab === 'eccezioni' &&
                    eccezioniRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={tableColSpan} align="center">
                          <Typography variant="body2" color="text.secondary">Nessuna eccezione disponibile</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </TableContainer>
            {tab === 'assegnazioni' && sourceMode === 'api' && (
              <TablePagination
                component="div"
                rowsPerPageOptions={[10, 25, 50, 100]}
                count={assegnazioniTotal}
                page={assegnazioniPage}
                onPageChange={(_event, newPage) => setAssegnazioniPage(newPage)}
                rowsPerPage={assegnazioniRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setAssegnazioniRowsPerPage(parseInt(event.target.value, 10));
                  setAssegnazioniPage(0);
                }}
                labelRowsPerPage="Righe per pagina:"
              />
            )}
            {tab === 'eccezioni' && sourceMode === 'api' && (
              <TablePagination
                component="div"
                rowsPerPageOptions={[10, 25, 50, 100]}
                count={eccezioniTotal}
                page={eccezioniPage}
                onPageChange={(_event, newPage) => setEccezioniPage(newPage)}
                rowsPerPage={eccezioniRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setEccezioniRowsPerPage(parseInt(event.target.value, 10));
                  setEccezioniPage(0);
                }}
                labelRowsPerPage="Righe per pagina:"
              />
            )}
          </StyledCard>
        </Grid>

        {tab === 'profili' && (
          <Grid item xs={12} md={12}>
            <StyledCard sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Dettaglio profilo selezionato</Typography>
              {profiloSelezionato ? (
                <>
                  <Typography variant="body2"><strong>Nome:</strong> {profiloSelezionato.nome}</Typography>
                  <Typography variant="body2"><strong>Tipologia:</strong> {profiloSelezionato.tipologia}</Typography>
                  <Typography variant="body2"><strong>Ore settimanali:</strong> {profiloSelezionato.ore_settimanali}</Typography>
                  <Typography variant="body2"><strong>Flessibile:</strong> {profiloSelezionato.flessibile}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Fasce settimanali:</strong>
                  </Typography>
                  {(fascePerProfilo[String(profiloSelezionato.id)] || []).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Nessuna fascia configurata
                    </Typography>
                  )}
                  {(fascePerProfilo[String(profiloSelezionato.id)] || []).length > 0 && (
                    <TableContainer sx={{ border: '1px solid rgba(145, 158, 171, 0.24)', borderRadius: 1.5, mt: 0.75 }}>
                      <Table size="small" sx={{ tableLayout: 'fixed' }}>
                        <TableHead sx={{ bgcolor: 'rgba(145, 158, 171, 0.08)' }}>
                          <TableRow>
                            {GIORNI_SETTIMANA.map((g) => (
                              <TableCell key={g.key} align="center" sx={{ fontWeight: 700 }}>
                                {g.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            {GIORNI_SETTIMANA.map((g) => (
                              <TableCell key={`fasce-day-${g.key}`} sx={{ verticalAlign: 'top' }}>
                                {(fasceDettaglioByDay[g.key] || []).length === 0 ? (
                                  <Typography variant="caption" color="text.secondary">-</Typography>
                                ) : (
                                  <Box sx={{ display: 'grid', gap: 0.25 }}>
                                    {(fasceDettaglioByDay[g.key] || []).map((slot) => (
                                      <Typography key={`${g.key}-${slot.ordine}-${slot.label}`} variant="caption">
                                        {slot.label}
                                      </Typography>
                                    ))}
                                  </Box>
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
                    Ultime eccezioni
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Seleziona un profilo per vedere il dettaglio.
                </Typography>
              )}
              {eccezioniRows.map((item) => (
                <Typography key={item.id || `${item.dipendente}-${item.data}`} variant="body2" sx={{ mb: 0.75 }}>
                  {item.data} - <strong>{item.dipendente}</strong> - lavorativo: {item.lavorativo} ({item.motivo})
                </Typography>
              ))}
            </StyledCard>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (saving) return;
          setDeleteConfirmOpen(false);
          setDeleteTarget(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 1 }}>Conferma eliminazione</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Stai per eliminare definitivamente:
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {deleteTargetTab === 'profili' && `Profilo: ${deleteTarget?.nome || '-'}`}
            {deleteTargetTab === 'assegnazioni' && `Assegnazione: ${deleteTarget?.dipendente || '-'} -> ${deleteTarget?.profilo || '-'}`}
            {deleteTargetTab === 'eccezioni' && `Eccezione: ${deleteTarget?.dipendente || '-'} (${deleteTarget?.data || '-'})`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            L&apos;operazione non può essere annullata.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setDeleteTarget(null);
            }}
            disabled={saving}
          >
            Annulla
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={closeCreateDialog}
        fullWidth
        maxWidth={tab === 'profili' ? 'md' : 'sm'}
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          {tab === 'profili' && (
            <Box sx={{ display: 'grid', gap: 1.25 }}>
              <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: 'repeat(2,minmax(0,1fr))' } }}>
                <TextField
                  label="Nome profilo"
                  size="small"
                  value={formProfilo.nome_profilo}
                  onChange={(e) => setFormProfilo((prev) => ({ ...prev, nome_profilo: e.target.value }))}
                  fullWidth
                />
                <FormControl size="small" fullWidth>
                  <InputLabel id="tipologia-label">Tipologia</InputLabel>
                  <Select
                    labelId="tipologia-label"
                    label="Tipologia"
                    value={formProfilo.tipologia_orario}
                    onChange={(e) => setFormProfilo((prev) => ({ ...prev, tipologia_orario: e.target.value }))}
                  >
                    <MenuItem value="fisso">fisso</MenuItem>
                    <MenuItem value="flessibile">flessibile</MenuItem>
                    <MenuItem value="part_time_mattina">part_time_mattina</MenuItem>
                    <MenuItem value="part_time_pomeriggio">part_time_pomeriggio</MenuItem>
                    <MenuItem value="altro">altro</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Ore settimanali (automatico)"
                  size="small"
                  value={formatMinutesAsHours(weeklyMinutesForm)}
                  InputProps={{ readOnly: true }}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel id="flessibile-label">Flessibile</InputLabel>
                  <Select
                    labelId="flessibile-label"
                    label="Flessibile"
                    value={formProfilo.flessibile}
                    onChange={(e) => setFormProfilo((prev) => ({ ...prev, flessibile: e.target.value }))}
                  >
                    <MenuItem value={0}>No</MenuItem>
                    <MenuItem value={1}>Si</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                Fasce settimanali (es. 09:00-13:00 e 13:40-18:00)
              </Typography>
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Tabs
                  value={giornoFasceTab}
                  onChange={(_, value) => setGiornoFasceTab(value)}
                  variant="fullWidth"
                  sx={{ borderBottom: '1px solid rgba(145, 158, 171, 0.24)' }}
                >
                  {GIORNI_SETTIMANA.map((g) => (
                    <Tab key={g.key} value={g.key} label={g.label} sx={{ minWidth: 0 }} />
                  ))}
                </Tabs>

                <Box sx={{ p: 1, border: '1px solid rgba(145, 158, 171, 0.24)', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {GIORNO_LABEL_COMPLETO[giornoFasceTab] || 'Giorno'}
                    </Typography>
                    <Button size="small" onClick={() => addFasciaSlot(giornoFasceTab)}>+ Aggiungi fascia</Button>
                  </Box>
                  <Box sx={{ display: 'grid', gap: 1 }}>
                    {(formFasce[giornoFasceTab] || []).map((slot, idx) => (
                      <Box
                        key={`${giornoFasceTab}-${idx}`}
                        sx={{
                          display: 'grid',
                          gap: 1,
                          gridTemplateColumns: { xs: '1fr 1fr auto', md: 'minmax(0,1fr) minmax(0,1fr) auto' },
                          alignItems: 'center'
                        }}
                      >
                        <TextField
                          size="small"
                          type="time"
                          label={`Inizio ${idx + 1}`}
                          InputLabelProps={{ shrink: true }}
                          value={slot.start || ''}
                          onChange={(e) => updateFasciaSlot(giornoFasceTab, idx, 'start', e.target.value)}
                        />
                        <TextField
                          size="small"
                          type="time"
                          label={`Fine ${idx + 1}`}
                          InputLabelProps={{ shrink: true }}
                          value={slot.end || ''}
                          onChange={(e) => updateFasciaSlot(giornoFasceTab, idx, 'end', e.target.value)}
                        />
                        <Button
                          color="error"
                          size="small"
                          onClick={() => removeFasciaSlot(giornoFasceTab, idx)}
                        >
                          Rimuovi
                        </Button>
                      </Box>
                    ))}
                    {(formFasce[giornoFasceTab] || []).length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Nessuna fascia per questo giorno
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Totale da fasce: <strong>{formatMinutesAsHours(weeklyMinutesForm)}</strong>
              </Typography>
            </Box>
          )}

          {tab === 'assegnazioni' && (
            <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: 'repeat(2,minmax(0,1fr))' } }}>
              <Autocomplete
                size="small"
                options={dipendentiOptions}
                getOptionLabel={(option) => option.label || ''}
                value={dipendentiOptions.find((d) => String(d.id) === String(formAssegnazione.dipendente_id)) || null}
                onChange={(_, selected) =>
                  setFormAssegnazione((prev) => ({ ...prev, dipendente_id: selected ? String(selected.id) : '' }))
                }
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                renderInput={(params) => <TextField {...params} label="Dipendente" />}
              />
              <FormControl size="small" fullWidth>
                <InputLabel id="ass-prof-label">Profilo</InputLabel>
                <Select
                  labelId="ass-prof-label"
                  label="Profilo"
                  value={formAssegnazione.profilo_orario_id}
                  onChange={(e) => setFormAssegnazione((prev) => ({ ...prev, profilo_orario_id: e.target.value }))}
                >
                  {profiliRows.map((p) => (
                    <MenuItem key={p.id || p.nome} value={String(p.id || '')}>{p.nome}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Data inizio"
                type="date"
                size="small"
                value={formAssegnazione.data_inizio}
                onChange={(e) => setFormAssegnazione((prev) => ({ ...prev, data_inizio: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Data fine"
                type="date"
                size="small"
                value={formAssegnazione.data_fine}
                onChange={(e) => setFormAssegnazione((prev) => ({ ...prev, data_fine: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}

          {tab === 'eccezioni' && (
            <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: 'repeat(2,minmax(0,1fr))' } }}>
              <Autocomplete
                size="small"
                options={dipendentiOptions}
                getOptionLabel={(option) => option.label || ''}
                value={dipendentiOptions.find((d) => String(d.id) === String(formEccezione.dipendente_id)) || null}
                onChange={(_, selected) =>
                  setFormEccezione((prev) => ({ ...prev, dipendente_id: selected ? String(selected.id) : '' }))
                }
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                renderInput={(params) => <TextField {...params} label="Dipendente" />}
              />
              <TextField
                label="Data eccezione"
                type="date"
                size="small"
                value={formEccezione.data_eccezione}
                onChange={(e) => setFormEccezione((prev) => ({ ...prev, data_eccezione: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl size="small" fullWidth>
                <InputLabel id="ecc-lav-label">Lavorativo</InputLabel>
                <Select
                  labelId="ecc-lav-label"
                  label="Lavorativo"
                  value={formEccezione.lavorativo}
                  onChange={(e) => setFormEccezione((prev) => ({ ...prev, lavorativo: e.target.value }))}
                >
                  <MenuItem value={1}>Si</MenuItem>
                  <MenuItem value={0}>No</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Motivo"
                size="small"
                value={formEccezione.motivo}
                onChange={(e) => setFormEccezione((prev) => ({ ...prev, motivo: e.target.value }))}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} disabled={saving}>Annulla</Button>
          <Button onClick={handleCreateSubmit} variant="contained" disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
